import React from 'react';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

/* Numeración FDI (WHO) — 32 piezas permanentes, cuadrantes 1-4 */
const CUADRANTES = [1, 2, 3, 4];
const numerosFDI = () => {
  const out = [];
  for (const cuad of CUADRANTES) for (let n = 8; n >= 1; n--) out.push(String(cuad * 10 + n));
  return out;
};

const ESTADOS_PIEZA = [
  { cod: 'SAN', label: 'Sana' },
  { cod: 'AUS', label: 'Ausente' },
  { cod: 'EXT', label: 'Extracción indicada' },
  { cod: 'END', label: 'Endodoncia' },
  { cod: 'COR', label: 'Corona' },
  { cod: 'IMP', label: 'Implante' },
  { cod: 'FRA', label: 'Fractura' },
];

const ESTADOS_SUPERFICIE = [
  { cod: 'SAN', label: 'Sana' },
  { cod: 'CAR', label: 'Caries activa' },
  { cod: 'OBT', label: 'Obturado' },
];

/* rojo = pendiente/problema, azul = tratamiento completado — convención LatAm */
const colorEstadoPieza = cod => ({
  SAN: 'var(--text3)',
  AUS: 'var(--text3)',
  EXT: 'var(--red)',
  FRA: 'var(--red)',
  END: 'var(--blue)',
  COR: 'var(--blue)',
  IMP: 'var(--blue)',
}[cod] || 'var(--text3)');

const colorEstadoSuperficie = cod => ({
  SAN: 'var(--bg)',
  CAR: 'var(--red)',
  OBT: 'var(--blue)',
}[cod] || 'var(--bg)');

/* Icono de diente en SVG, coloreado según estado whole-tooth */
function DienteSVG({ estado, ausente }) {
  const color = colorEstadoPieza(estado);
  if (ausente || estado === 'AUS') {
    return (
      <svg viewBox="0 0 40 48" width="34" height="40">
        <path d="M20 8 C10 8 6 16 8 26 C9 33 13 40 17 40 C19 40 19 33 20 30 C21 33 21 40 23 40 C27 40 31 33 32 26 C34 16 30 8 20 8 Z"
          fill="none" stroke="var(--border)" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.5" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 40 48" width="34" height="40">
      <path d="M20 8 C10 8 6 16 8 26 C9 33 13 40 17 40 C19 40 19 33 20 30 C21 33 21 40 23 40 C27 40 31 33 32 26 C34 16 30 8 20 8 Z"
        fill={estado === 'SAN' ? 'var(--surface)' : color}
        fillOpacity={estado === 'SAN' ? 1 : 0.18}
        stroke={color} strokeWidth="2" />
    </svg>
  );
}

export default function Odontograma() {
  const [sp] = useSearchParams();
  const { usuario } = useAuth();
  const [codPac, setCodPac]   = useState(sp.get('paciente') || '');
  const [pacBusq, setPacBusq] = useState('');
  const [pacSugs, setPacSugs] = useState([]);
  const [paciente, setPac]    = useState(null);
  const [overview, setOverview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [piezaSel, setPiezaSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [loadDet, setLoadDet] = useState(false);
  const [superficieSel, setSuperficieSel] = useState(null);
  const [tab, setTab] = useState('overview'); // 'overview' | 'historial'
  const [historial, setHistorial] = useState([]);
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const [formPieza, setFormPieza] = useState({ C_ESTADO_PIEZA: 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
  const [formSup, setFormSup] = useState({ C_ESTADO_SUPERFICIE: 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });

  useEffect(() => {
    if (!pacBusq.trim()) { setPacSugs([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/pacientes?buscar=${encodeURIComponent(pacBusq)}`);
        setPacSugs(data.slice ? data.slice(0, 8) : []);
      } catch (e) { setPacSugs([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [pacBusq]);

  useEffect(() => {
    if (!codPac) return;
    cargarPaciente();
    cargarOverview();
  }, [codPac]);

  const cargarPaciente = async () => {
    try {
      const { data } = await api.get(`/pacientes/${codPac}`);
      setPac(data);
    } catch (e) { /* silencioso */ }
  };

  const cargarOverview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/odontograma/${codPac}`);
      setOverview(data);
    } catch (e) {
      setMsg('✕ No se pudo cargar el odontograma');
    } finally {
      setLoading(false);
    }
  };

  const abrirPieza = async numeroPieza => {
    setPiezaSel(numeroPieza);
    setSuperficieSel(null);
    setLoadDet(true);
    try {
      const { data } = await api.get(`/odontograma/pieza/${codPac}/${numeroPieza}`);
      setDetalle(data);
      setFormPieza({ C_ESTADO_PIEZA: data.estado || 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
    } catch (e) {
      setMsg('✕ No se pudo cargar la pieza');
    } finally {
      setLoadDet(false);
    }
  };

  const guardarPieza = async () => {
    if (!formPieza.TRATAMIENTO.trim()) { setMsg('✕ Describe el tratamiento realizado'); return; }
    setSaving(true); setMsg('');
    try {
      await api.post('/odontograma/tratamiento', {
        COD_PACIENTE: codPac, NUMERO_PIEZA: piezaSel,
        C_ESTADO_PIEZA: formPieza.C_ESTADO_PIEZA,
        TRATAMIENTO: formPieza.TRATAMIENTO,
        OBSERVACIONES: formPieza.OBSERVACIONES || null,
      });
      setMsg('✓ Pieza actualizada');
      await cargarOverview();
      await abrirPieza(piezaSel);
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const abrirSuperficie = superficie => {
    setSuperficieSel(superficie);
    const actual = detalle?.superficies?.find(s => s.superficie === superficie);
    setFormSup({ C_ESTADO_SUPERFICIE: actual?.estado || 'SAN', TRATAMIENTO: '', OBSERVACIONES: '' });
  };

  const guardarSuperficie = async () => {
    if (!formSup.TRATAMIENTO.trim()) { setMsg('✕ Describe el tratamiento realizado'); return; }
    setSaving(true); setMsg('');
    try {
      await api.post('/odontograma/superficie', {
        COD_PACIENTE: codPac, NUMERO_PIEZA: piezaSel, C_SUPERFICIE: superficieSel,
        C_ESTADO_SUPERFICIE: formSup.C_ESTADO_SUPERFICIE,
        TRATAMIENTO: formSup.TRATAMIENTO,
        OBSERVACIONES: formSup.OBSERVACIONES || null,
      });
      setMsg('✓ Superficie actualizada');
      await cargarOverview();
      await abrirPieza(piezaSel);
      setSuperficieSel(null);
    } catch (e) {
      setMsg('✕ ' + (e.response?.data?.error || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const cargarHistorial = async () => {
    try {
      const { data } = await api.get(`/odontograma/tratamientos/${codPac}`);
      setHistorial(data);
    } catch (e) { setHistorial([]); }
  };

  useEffect(() => {
    if (tab === 'historial' && codPac) cargarHistorial();
  }, [tab, codPac]);

  const hallazgoDe = numero => overview.find(o => o.numeroPieza === numero);

  /* ═══ SIN PACIENTE ═══ */
  if (!codPac) {
    return (
      <>
        <style>{css}</style>
        <div className="od-search-wrap">
          <h2 className="od-search-title">Odontograma — buscar paciente</h2>
          <input className="od-search-input" placeholder="Buscar por nombre o cédula..."
            value={pacBusq} onChange={e => setPacBusq(e.target.value)} autoFocus />
          {pacSugs.length > 0 && (
            <div className="od-search-sugs">
              {pacSugs.map(p => (
                <div key={p.COD} className="od-search-sug"
                  onClick={() => { setCodPac(String(p.COD)); setPacBusq(''); setPacSugs([]); }}>
                  <div className="od-search-sug-nom">{[p.APELLIDO_PATERNO, p.APELLIDO_MATERNO, p.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
                  <div className="od-search-sug-ci">CI: {p.IDENTIFICACION || '—'}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  }

  /* ═══ CON PACIENTE ═══ */
  return (
    <>
      <style>{css}</style>
      {msg && <div className={`od-msg ${msg.startsWith('✓') ? 'ok' : 'err'}`}>{msg}</div>}

      <div className="od-page">
        <div className="od-hdr">
          <div>
            <div className="od-hdr-name">{[paciente?.APELLIDO_PATERNO, paciente?.APELLIDO_MATERNO, paciente?.PRIMER_NOMBRE].filter(Boolean).join(' ')}</div>
            <div className="od-hdr-sub">CI: {paciente?.IDENTIFICACION || '—'}</div>
          </div>
          <button className="btn-od-sec" onClick={() => { setCodPac(''); setPac(null); setOverview([]); setDetalle(null); setPiezaSel(null); }}>
            Cambiar paciente
          </button>
        </div>

        <div className="od-tabs">
          <div className={`od-tab ${tab === 'overview' ? 'act' : ''}`} onClick={() => setTab('overview')}>
            <i className="ti ti-dental" aria-hidden="true"></i> Odontograma
          </div>
          <div className={`od-tab ${tab === 'historial' ? 'act' : ''}`} onClick={() => setTab('historial')}>
            <i className="ti ti-history" aria-hidden="true"></i> Historial
          </div>
        </div>

        {tab === 'overview' && (
          <div className="od-body">
            <div className="od-legend">
              <span><i className="od-dot" style={{ background: 'var(--text3)', opacity: .3 }}></i> Sana</span>
              <span><i className="od-dot" style={{ background: 'var(--red)' }}></i> Pendiente / problema</span>
              <span><i className="od-dot" style={{ background: 'var(--blue)' }}></i> Tratamiento completado</span>
              <span><i className="od-dot" style={{ border: '1px dashed var(--border)', background: 'transparent' }}></i> Ausente</span>
            </div>

            {loading ? <div className="spin-wrap"><div className="sp"/></div> : (
              <div className="od-arcada-wrap">
                {[[1,2],[4,3]].map((par, i) => (
                  <div key={i} className="od-cuadrante-fila">
                    {par.map(cuad => (
                      <div key={cuad} className="od-cuadrante">
                        {numerosFDI().filter(n => n[0] === String(cuad)).map(numero => {
                          const h = hallazgoDe(numero);
                          return (
                            <button key={numero}
                              className={`od-diente-btn ${piezaSel === numero ? 'sel' : ''}`}
                              onClick={() => abrirPieza(numero)}>
                              <DienteSVG estado={h?.estado} ausente={h?.estado === 'AUS'} />
                              <span className="od-diente-num">{numero}</span>
                              {h?.tieneHallazgo && <span className="od-diente-flag" title="Tiene hallazgos por superficie" />}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {piezaSel && (
              <div className="od-detalle">
                <div className="od-detalle-hdr">
                  <span>Pieza {piezaSel}</span>
                  <button className="btn-mo-sec" style={{ padding: '4px 10px' }} onClick={() => { setPiezaSel(null); setDetalle(null); }}>✕</button>
                </div>

                {loadDet ? <div className="spin-wrap"><div className="sp"/></div> : detalle && (
                  <div className="od-detalle-body">
                    <div className="od-detalle-col">
                      <div className="mo-sec">Estado general de la pieza</div>
                      <div className="od-chip-row">
                        {ESTADOS_PIEZA.map(e => (
                          <button key={e.cod} type="button"
                            className={`od-chip ${formPieza.C_ESTADO_PIEZA === e.cod ? 'act' : ''}`}
                            onClick={() => setFormPieza(f => ({ ...f, C_ESTADO_PIEZA: e.cod }))}>
                            {e.label}
                          </button>
                        ))}
                      </div>
                      <div className="mo-sec" style={{ marginTop: 10 }}>Tratamiento realizado *</div>
                      <input className="form-control" placeholder="Ej: Extracción simple pieza 18"
                        value={formPieza.TRATAMIENTO} onChange={e => setFormPieza(f => ({ ...f, TRATAMIENTO: e.target.value }))} />
                      <div className="mo-sec" style={{ marginTop: 10 }}>Observaciones</div>
                      <textarea className="form-control" rows={2}
                        value={formPieza.OBSERVACIONES} onChange={e => setFormPieza(f => ({ ...f, OBSERVACIONES: e.target.value }))} />
                      <button className="btn-mo-pri" style={{ marginTop: 10 }} disabled={saving} onClick={guardarPieza}>
                        {saving ? 'Guardando...' : 'Guardar estado de la pieza'}
                      </button>
                    </div>

                    <div className="od-detalle-col">
                      <div className="mo-sec">Superficies (clic para marcar hallazgo)</div>
                      <div className="od-cruz">
                        <button className="od-sup od-sup-v" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'V')?.estado) }}
                          onClick={() => abrirSuperficie('V')}>V</button>
                        <button className="od-sup od-sup-m" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'M')?.estado) }}
                          onClick={() => abrirSuperficie('M')}>M</button>
                        <div className="od-sup od-sup-centro">{piezaSel}</div>
                        <button className="od-sup od-sup-o" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'O')?.estado) }}
                          onClick={() => abrirSuperficie('O')}>O</button>
                        <button className="od-sup od-sup-d" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'D')?.estado) }}
                          onClick={() => abrirSuperficie('D')}>D</button>
                        <button className="od-sup od-sup-l" style={{ background: colorEstadoSuperficie(detalle.superficies.find(s => s.superficie === 'L')?.estado) }}
                          onClick={() => abrirSuperficie('L')}>L</button>
                      </div>

                      {superficieSel && (
                        <div className="od-sup-form">
                          <div className="mo-sec">Superficie {superficieSel}</div>
                          <div className="od-chip-row">
                            {ESTADOS_SUPERFICIE.map(e => (
                              <button key={e.cod} type="button"
                                className={`od-chip ${formSup.C_ESTADO_SUPERFICIE === e.cod ? 'act' : ''}`}
                                onClick={() => setFormSup(f => ({ ...f, C_ESTADO_SUPERFICIE: e.cod }))}>
                                {e.label}
                              </button>
                            ))}
                          </div>
                          <div className="mo-sec" style={{ marginTop: 10 }}>Tratamiento realizado *</div>
                          <input className="form-control" placeholder="Ej: Obturación resina compuesta"
                            value={formSup.TRATAMIENTO} onChange={e => setFormSup(f => ({ ...f, TRATAMIENTO: e.target.value }))} />
                          <div className="mo-sec" style={{ marginTop: 10 }}>Observaciones</div>
                          <textarea className="form-control" rows={2}
                            value={formSup.OBSERVACIONES} onChange={e => setFormSup(f => ({ ...f, OBSERVACIONES: e.target.value }))} />
                          <button className="btn-mo-pri" style={{ marginTop: 10 }} disabled={saving} onClick={guardarSuperficie}>
                            {saving ? 'Guardando...' : 'Guardar superficie'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {tab === 'historial' && (
          <div className="od-body">
            {historial.length === 0 ? (
              <div className="empty-hc">Sin tratamientos registrados</div>
            ) : (
              <div className="od-hist-list">
                {historial.map(h => (
                  <div key={h.COD} className="od-hist-row">
                    <div className="od-hist-fecha">{h.FECHA?.slice(0,10)}</div>
                    <div className="od-hist-body">
                      <div className="od-hist-tratamiento">Pieza {h.NUMERO_PIEZA}{h.C_SUPERFICIE ? ` · Sup. ${h.C_SUPERFICIE}` : ''} — {h.TRATAMIENTO}</div>
                      <div className="od-hist-sub">{h.USUARIO_NOMBRE || h.USUARIO_REGISTRO} · Estado resultante: {h.C_ESTADO_RESULTANTE}</div>
                      {h.OBSERVACIONES && <div className="od-hist-obs">{h.OBSERVACIONES}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

const css = `
.od-search-wrap { max-width:440px; margin:60px auto; text-align:center; }
.od-search-title { font-size:16px; font-weight:700; color:var(--text); margin-bottom:16px; }
.od-search-input { width:100%; padding:10px 14px; border:1px solid var(--border); border-radius:9px; font-size:14px; background:var(--surface); color:var(--text); outline:none; }
.od-search-input:focus { border-color:var(--navy-lt); }
.od-search-sugs { margin-top:8px; border:1px solid var(--border); border-radius:9px; overflow:hidden; text-align:left; background:var(--surface); }
.od-search-sug { padding:9px 14px; cursor:pointer; border-bottom:1px solid var(--bg); }
.od-search-sug:hover { background:var(--bg); }
.od-search-sug-nom { font-size:13px; font-weight:600; color:var(--text); }
.od-search-sug-ci { font-size:11px; color:var(--text3); }

.od-msg { padding:8px 14px; border-radius:8px; font-size:12px; margin-bottom:8px; }
.od-msg.ok  { background:var(--green-lt); color:var(--green); border:1px solid var(--green); }
.od-msg.err { background:var(--red-lt);   color:var(--red);   border:1px solid var(--red); }

.od-page { display:flex; flex-direction:column; gap:12px; }
.od-hdr  { display:flex; align-items:center; justify-content:space-between; background:var(--navy); color:#fff; padding:14px 18px; border-radius:12px; }
.od-hdr-name { font-size:15px; font-weight:700; }
.od-hdr-sub  { font-size:12px; opacity:.8; margin-top:2px; }

.od-tabs { display:flex; gap:4px; border-bottom:1px solid var(--border); }
.od-tab  { padding:9px 16px; font-size:13px; cursor:pointer; color:var(--text3); border-bottom:2px solid transparent; display:flex; align-items:center; gap:6px; }
.od-tab.act { color:var(--navy); border-bottom-color:var(--navy-lt); font-weight:600; }

.od-body { display:flex; flex-direction:column; gap:14px; }
.od-legend { display:flex; gap:16px; flex-wrap:wrap; font-size:11px; color:var(--text2); }
.od-dot { display:inline-block; width:10px; height:10px; border-radius:50%; margin-right:5px; vertical-align:-1px; }

.od-arcada-wrap { display:flex; flex-direction:column; gap:18px; background:var(--surface); border:1px solid var(--border); border-radius:12px; padding:18px; overflow-x:auto; }
.od-cuadrante-fila { display:flex; justify-content:center; gap:24px; }
.od-cuadrante { display:flex; gap:2px; }
.od-diente-btn { position:relative; display:flex; flex-direction:column; align-items:center; gap:2px; background:none; border:1px solid transparent; border-radius:8px; padding:4px 2px; cursor:pointer; }
.od-diente-btn:hover { background:var(--bg); }
.od-diente-btn.sel { border-color:var(--navy-lt); background:var(--blue-lt); }
.od-diente-num { font-size:9px; color:var(--text3); }
.od-diente-flag { position:absolute; top:0; right:2px; width:7px; height:7px; border-radius:50%; background:var(--amber); }

.od-detalle { border:1px solid var(--border); border-radius:12px; overflow:hidden; }
.od-detalle-hdr { padding:9px 14px; background:var(--surface2); border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; font-size:13px; font-weight:700; color:var(--text); }
.od-detalle-body { display:grid; grid-template-columns:1fr 1fr; gap:16px; padding:16px; }
@media (max-width:720px) { .od-detalle-body { grid-template-columns:1fr; } }
.od-detalle-col { display:flex; flex-direction:column; }

.od-chip-row { display:flex; flex-wrap:wrap; gap:6px; }
.od-chip { padding:5px 11px; border-radius:20px; border:1px solid var(--border); background:var(--bg); font-size:11px; cursor:pointer; color:var(--text2); }
.od-chip.act { background:var(--navy); border-color:var(--navy); color:#fff; }

.od-cruz { position:relative; width:180px; height:180px; margin:10px auto; }
.od-sup { position:absolute; border:1px solid var(--border); cursor:pointer; font-size:11px; font-weight:700; color:var(--text); display:flex; align-items:center; justify-content:center; }
.od-sup-v { top:0; left:50px; width:80px; height:50px; border-radius:8px 8px 0 0; }
.od-sup-m { left:0; top:50px; width:50px; height:80px; border-radius:8px 0 0 8px; }
.od-sup-d { right:0; top:50px; width:50px; height:80px; border-radius:0 8px 8px 0; }
.od-sup-l { bottom:0; left:50px; width:80px; height:50px; border-radius:0 0 8px 8px; }
.od-sup-o { left:50px; top:50px; width:80px; height:80px; }
.od-sup-centro { position:absolute; left:50px; top:50px; width:80px; height:80px; display:flex; align-items:center; justify-content:center; font-size:10px; color:var(--text3); pointer-events:none; }
.od-sup-form { margin-top:12px; padding-top:12px; border-top:1px solid var(--border); }

.od-hist-list { display:flex; flex-direction:column; gap:8px; }
.od-hist-row { display:flex; gap:12px; padding:10px 14px; border:1px solid var(--border); border-radius:9px; background:var(--surface); }
.od-hist-fecha { font-size:11px; color:var(--text3); flex-shrink:0; width:70px; }
.od-hist-tratamiento { font-size:13px; font-weight:600; color:var(--text); }
.od-hist-sub { font-size:11px; color:var(--text3); margin-top:2px; }
.od-hist-obs { font-size:11px; color:var(--text2); margin-top:4px; font-style:italic; }
`;
